/**
 * 回傳網站共用使用的資料
 *
 * @returns { name: 網站名稱, domain: 網站網域, blobDomain: 網站Blob網域, captchaSiteKey: Captcha Site Key }
 */

import _ from 'lodash';

import CONFIG from '@/assets/data/config.json';

export function useWebsite() {
  const runtimeConfig = useRuntimeConfig();
  const { ENVIRONMENT, CAPTCHA_SITEKEY_GLOBAL, CAPTCHA_SITEKEY_ACN } =
    runtimeConfig.public;

  const { $i18n } = useNuxtApp();
  const route = useRoute();
  const { isCN } = useLanguage();
  const { isMobile } = useScreen();

  const { t, locale } = $i18n;

  // 根據目前語系回傳網站名稱
  const name = computed((): string =>
    isCN.value ? '研华工业云' : 'Advantech WISE'
  );

  // 根據目前語系回傳網站的網域
  const domain = computed((): string =>
    provideDomain(locale.value as EnumLocale, ENVIRONMENT)
  );

  // 根據目前語系回傳網站的網域
  const consoleDomain = computed((): string =>
    provideConsoleDomain(locale.value as EnumLocale, ENVIRONMENT, true)
  );

  const consoleDomainWithoutHttps = computed((): string =>
    provideConsoleDomain(locale.value as EnumLocale, ENVIRONMENT, false)
  );

  // 根據目前語系回傳網站的Blob網域
  const blobDomain = computed((): string =>
    provideBlobDomain(locale.value as EnumLocale, ENVIRONMENT, true)
  );

  const blobDomainWithoutHttps = computed((): string =>
    provideBlobDomain(locale.value as EnumLocale, ENVIRONMENT, false)
  );

  // Captcha Site Key
  const captchaSiteKey = computed((): string => {
    return (isCN.value ? CAPTCHA_SITEKEY_ACN : CAPTCHA_SITEKEY_GLOBAL) || '';
  });

  // 根據目前裝置回傳 header 高度
  const headerHeight = computed((): number => {
    return isMobile.value
      ? CONFIG.headerHeight.mobile
      : CONFIG.headerHeight.desktop;
  });

  // Navigation Bar 的高度
  const navigationHeight = computed((): number => {
    return isMobile.value
      ? CONFIG.navigationHeight.mobile
      : CONFIG.navigationHeight.desktop;
  });

  // 根據語系與環境回傳用於 canonical 及 alternate 標籤的標準網址

  const utmParams: string[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ];

  const redirectMap: { acnPath: string; globalPath: string }[] = [
    {
      acnPath: '/overview',
      globalPath: '/about-us',
    },
    {
      acnPath: '/ecosystem/overview',
      globalPath: '/partners',
    },
    {
      acnPath: '/ecosystem/list',
      globalPath: '/partners/Ecosystem',
    },
  ];

  const normalizePath = (locale: EnumLocale, path: string): string => {
    const prefix: string = '/marketplace';
    const hasPrefix: boolean = path.startsWith(prefix);
    const isCN: boolean = locale === EnumLocale.ZH_CN;

    if (isCN && !hasPrefix) {
      path = `${prefix}${path}`;
    }
    if (!isCN && hasPrefix) {
      path = path.slice(prefix.length);
    }

    const purePath = path.startsWith(prefix) ? path.slice(prefix.length) : path;

    for (const { acnPath, globalPath } of redirectMap) {
      if (isCN && purePath === acnPath) {
        path = `${prefix}${globalPath}`;
        break;
      }
      if (!isCN && purePath === globalPath) {
        path = acnPath;
        break;
      }
    }
    return path;
  };

  const provideTargetUrl = (locale: EnumLocale): string => {
    const domain = provideDomain(locale, ENVIRONMENT);
    const withoutLocalePath = route.path.split('/').slice(2).join('/');
    const path = withoutLocalePath === '' ? '' : `/${withoutLocalePath}`;
    const normalizedPath = normalizePath(locale, path);

    // 移除 UTM 參數，並將其他參數轉換回 query string
    const withoutUtmQuery = _.omit(route.query, utmParams);
    const queryParts = Object.entries(withoutUtmQuery).flatMap(
      ([key, value]) => {
        if (Array.isArray(value)) return value.map((v) => `${key}=${v}`);
        if (value === null) return [key];
        return [`${key}=${value}`];
      }
    );
    // 將 query string 中的 + 號轉換為 %2B、空格轉換為 +
    const queryPart = (() => {
      if (queryParts.length) {
        return `?${queryParts.join('&')}`
          .replace(/\+/g, '%2B')
          .replace(/ /g, '+');
      }
      return '';
    })();

    return `${domain}/${locale}${normalizedPath}${queryPart}`;
  };

  // 回傳用於網站標題的 title
  const provideWebTitle = (titleChunk?: string): string => {
    return titleChunk ? `${titleChunk} - ${name.value}` : t('meta.title');
  };

  return {
    name,
    domain,
    consoleDomain,
    consoleDomainWithoutHttps,
    blobDomain,
    blobDomainWithoutHttps,
    captchaSiteKey,
    headerHeight,
    navigationHeight,
    provideTargetUrl,
    provideWebTitle,
  };
}
